import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MarketNavbar from "../MarketNav/MarketNavbar";
import MarketplacePage from "../components/MarketplacePage";
import ProductGrid from "../components/ProductGrid";
import QuickAddSizeModal from "../components/QuickAddSizeModal";
import useSearchProducts from "../Merchant/lib/fetchSearchProduct";
import useFetchProductCategory from "../../../libs/getProductCategories";
import { CartContext } from "../../../Context/CartContext";
import useQuickAddToCart from "../libs/useQuickAddToCart";
import { ToastContainer } from "react-toastify";
import "../ProductCategory/ProductCategory.css";
import "../marketplace-layout.css";
import "react-toastify/dist/ReactToastify.css";

const SORT_OPTIONS = [
  { value: "name", label: "Name (A–Z)" },
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
];

const SearchResultsPage = () => {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") || "";
  const trimmedQuery = query.trim();
  const page = Math.max(parseInt(params.get("page") || "1", 10) || 1, 1);
  const sort = params.get("sort") || "name";
  const category = params.get("category") || "";
  const minPrice = params.get("minPrice") || "";
  const maxPrice = params.get("maxPrice") || "";
  const { addItemToCart } = useContext(CartContext);
  const { quickAdd, pending, confirmSize, cancelSize } = useQuickAddToCart(addItemToCart);
  const { data: categoryData } = useFetchProductCategory();

  const searchOptions = useMemo(
    () => ({
      limit: 24,
      page,
      sort,
      category: category || undefined,
      minPrice: minPrice || undefined,
      maxPrice: maxPrice || undefined,
    }),
    [page, sort, category, minPrice, maxPrice]
  );

  const { isLoading, error, searchResults } = useSearchProducts(query, searchOptions);
  const [accumulatedProducts, setAccumulatedProducts] = useState([]);

  useEffect(() => {
    const nextProducts = searchResults?.products || [];
    if (page <= 1) {
      setAccumulatedProducts(nextProducts);
    } else if (nextProducts.length > 0) {
      setAccumulatedProducts((prev) => {
        const seen = new Set(prev.map((item) => item._id));
        const merged = [...prev];
        nextProducts.forEach((item) => {
          if (!seen.has(item._id)) merged.push(item);
        });
        return merged;
      });
    }
  }, [searchResults?.products, page]);

  useEffect(() => {
    setAccumulatedProducts([]);
  }, [query, sort, category, minPrice, maxPrice]);

  const totalCount = searchResults?.count ?? 0;
  const totalPages = searchResults?.totalPages ?? 0;
  const hasMore = page < totalPages;
  const categories = categoryData?.categories || [];

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.set("page", "1");
    setParams(next);
  };

  const handleLoadMore = () => {
    updateParam("page", String(page + 1));
  };

  return (
    <Fragment>
      <MarketNavbar />
      <MarketplacePage>
        <div className="search-results-page">
          <nav className="search-results-breadcrumb" aria-label="Breadcrumb">
            <Link to="/market">Home</Link>
            <span>/</span>
            <span>Search</span>
          </nav>
          <h2 className="search-results-title">
            {query
              ? totalCount != null
                ? `${totalCount} result${totalCount === 1 ? "" : "s"} for “${query}”`
                : `Results for “${query}”`
              : "Search products"}
          </h2>

          {trimmedQuery.length >= 2 && (
            <div className="search-results-toolbar">
              <label className="search-filter">
                <span>Category</span>
                <select
                  value={category}
                  onChange={(e) => updateParam("category", e.target.value)}
                >
                  <option value="">All categories</option>
                  {categories.map((cat) => (
                    <option key={cat._id || cat.categoryName} value={cat.categoryName}>
                      {cat.categoryName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="search-filter">
                <span>Min price</span>
                <input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={minPrice}
                  onChange={(e) => updateParam("minPrice", e.target.value)}
                />
              </label>
              <label className="search-filter">
                <span>Max price</span>
                <input
                  type="number"
                  min="0"
                  placeholder="Any"
                  value={maxPrice}
                  onChange={(e) => updateParam("maxPrice", e.target.value)}
                />
              </label>
              <label className="search-filter">
                <span>Sort</span>
                <select value={sort} onChange={(e) => updateParam("sort", e.target.value)}>
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {isLoading && page === 1 && <p className="search-results-status">Searching…</p>}
          {error && (
            <p className="search-results-status search-results-status--error">{error}</p>
          )}
          {!isLoading && query && trimmedQuery.length < 2 && (
            <p className="search-results-status">Enter at least 2 characters to search.</p>
          )}
          {!isLoading && query && trimmedQuery.length >= 2 && accumulatedProducts.length === 0 && !error && (
            <div className="search-results-empty">
              <p>No products matched your search.</p>
              <Link to="/market">Browse marketplace</Link>
            </div>
          )}
          {accumulatedProducts.length > 0 && (
            <>
              <ProductGrid products={accumulatedProducts} onAddToCart={quickAdd} />
              {hasMore && (
                <div className="search-load-more">
                  <button type="button" onClick={handleLoadMore} disabled={isLoading}>
                    {isLoading ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </MarketplacePage>
      <QuickAddSizeModal pending={pending} onConfirm={confirmSize} onCancel={cancelSize} />
      <ToastContainer position="top-right" className="toast" />
    </Fragment>
  );
};

export default SearchResultsPage;
